import {
  getPaginationRowModel,
  getFilteredRowModel,
  ColumnFiltersState,
  getSortedRowModel,
  PaginationState,
  getCoreRowModel,
  useReactTable,
  SortingState,
} from '@ui/components';
import {
  OpensearchSearchRequestOpenSearchDataTypeEnum,
  OpensearchSearchResponseListObject,
} from '@interfaces/models';
import { useInfiniteQuery, useQueryClient, useQuery } from '@tanstack/react-query';
import { getOpensearchTableConfig, getOpensearchTableData } from '@api/opensearch';
import { getTableFilters, getTableSort } from '@shared/utils';
import { useCallback, useState, useMemo } from 'react';
import { useAutoColumns } from '@shared/components';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '@ui/hooks';

import { actionColumn } from './parking-spots-table-columns';

const initialPageParam = { pageSize: 50, scrollId: undefined };

export const useParkingSpotsTable = ({
  onStopParking,
}: {
  onStopParking?: (parkingId: string) => void;
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const debouncedTableSearchQuery = useDebounce(tableSearchQuery, 800);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const queryParams = useMemo(
    () => ({
      sort: getTableSort(sorting),
      filters: getTableFilters(2, debouncedTableSearchQuery),
    }),
    [sorting, debouncedTableSearchQuery],
  );

  const { data: tableConfig, isLoading: isTableConfigLoading } = useQuery({
    queryKey: ['parking-spots-table-config'],
    queryFn: () =>
      getOpensearchTableConfig(OpensearchSearchRequestOpenSearchDataTypeEnum.ACTIVE_PARKINGS),
  });

  const { columnDefinitions } = useAutoColumns({
    columns: tableConfig ?? [],
    actions: actionColumn(t, onStopParking),
  });

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    refetch: refetchTable,
  } = useInfiniteQuery({
    queryFn: ({ pageParam, signal }) => {
      return getOpensearchTableData(
        {
          openSearchDataType: OpensearchSearchRequestOpenSearchDataTypeEnum.ACTIVE_PARKINGS,
          ...queryParams,
          ...pageParam,
        },
        signal,
      );
    },
    getNextPageParam: (lastPage: OpensearchSearchResponseListObject) => {
      return {
        pageSize: 50,
        scrollId: lastPage.scrollId || undefined,
      };
    },
    queryKey: ['parking-spots-list', queryParams],
    initialPageParam,
  });

  const data = useMemo(
    () => infiniteData?.pages.flatMap((page) => page.data) ?? [],
    [infiniteData],
  );

  const totalItems = useMemo(() => infiniteData?.pages[0]?.totalHits ?? 0, [infiniteData]);

  const loadedItems = useMemo(() => {
    return data.length;
  }, [data]);

  const table = useReactTable({
    data: data ?? [],
    columns: columnDefinitions,
    state: { pagination, sorting, globalFilter: tableSearchQuery, columnFilters, rowSelection },
    pageCount: Math.ceil(totalItems / pagination.pageSize),
    rowCount: totalItems,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    getRowId: (row) => row[0] as string,
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: setTableSearchQuery,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
  });

  const resetTable = useCallback(() => {
    queryClient.resetQueries({ queryKey: ['parking-spots-list'] });
  }, [queryClient]);

  return {
    globalFilter: table.getState().globalFilter,
    columnsLength: columnDefinitions.length,
    isTableConfigLoading,
    fetchNextPage,
    refetchTable,
    hasNextPage,
    loadedItems,
    resetTable,
    totalItems,
    isFetching,
    isLoading,
    table,
  };
};
